import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../components/JWTAuthContext.js';
import { SuccessModal, ErrorModal } from '../components/common';
import { 
  sendMobileOTP, 
  verifyMobileOTP, 
  resendMobileOTP,
  getMobileVerificationStatus 
} from '../components/apiClient.js';

interface ModalData {
  title: string;
  message: string;
  buttonText: string;
}

const MobileVerification: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshAuthData } = useJWTAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  
  // Mobile input state
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileError, setMobileError] = useState('');
  
  // OTP input state
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  
  // Rate limiting and attempts state
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const [verificationAttemptsRemaining, setVerificationAttemptsRemaining] = useState(3);
  const [canResend, setCanResend] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [otpExpiryTime, setOtpExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalData, setModalData] = useState<ModalData>({
    title: '',
    message: '',
    buttonText: 'Okay'
  });

  // Check if user is already verified on mount
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        
        const response = await getMobileVerificationStatus();
        if (response.status === "success" && response.result.mobile_verified) {
          // Already verified, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Error checking verification status:', error);
      }
    };
    
    checkVerificationStatus();
  }, [navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  // OTP expiry countdown
  useEffect(() => {
    if (otpExpiryTime) {
      const updateTimer = () => {
        const now = new Date();
        const remaining = Math.max(0, Math.floor((otpExpiryTime.getTime() - now.getTime()) / 1000));
        setTimeRemaining(remaining);
        
        if (remaining === 0) {
          setOtpError('OTP has expired. Please request a new one.');
        }
      };
      
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [otpExpiryTime]);

  // Warning modal on navigation attempt
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Mobile verification is required to access your account. You cannot proceed without verifying your mobile number.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Validate Indian mobile number format
  const validateMobileNumber = (mobile: string): boolean => {
    // Remove any spaces or hyphens
    const cleanMobile = mobile.replace(/[\s-]/g, '');
    
    // Check if it's a 10-digit number starting with 6-9
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(cleanMobile);
  };

  // Handle mobile number input
  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 10) {
      setMobileNumber(value);
      setMobileError('');
    }
  };

  // Handle OTP input
  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 6) {
      setOtpCode(value);
      setOtpError('');
    }
  };

  // Send OTP
  const handleSendOTP = async () => {
    // Validate mobile number
    if (!mobileNumber) {
      setMobileError('Please enter your mobile number');
      return;
    }

    if (!validateMobileNumber(mobileNumber)) {
      setMobileError('Please enter a valid 10-digit Indian mobile number starting with 6-9');
      return;
    }

    if (attemptsRemaining <= 0) {
      setModalData({
        title: 'Rate Limit Reached',
        message: 'You have exceeded the maximum number of OTP requests for this mobile number. Please try again later or contact support.',
        buttonText: 'Okay'
      });
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    setMobileError('');

    try {
      const response = await sendMobileOTP(mobileNumber);
      
      if (response.status === "success") {
        const data = response.result;
        setAttemptsRemaining(data.attempts_remaining);
        setVerificationAttemptsRemaining(3); // Reset verification attempts for new OTP
        
        // Calculate expiry time
        const expiryDate = new Date();
        expiryDate.setMinutes(expiryDate.getMinutes() + data.expires_in_minutes);
        setOtpExpiryTime(expiryDate);
        
        // Set resend countdown (60 seconds)
        setResendCountdown(60);
        setCanResend(false);
        
        // Move to OTP step
        setStep('otp');
        
        setModalData({
          title: 'OTP Sent Successfully',
          message: `A 6-digit OTP has been sent to ${mobileNumber}. It will expire in ${data.expires_in_minutes} minutes.`,
          buttonText: 'Continue'
        });
        setShowSuccessModal(true);
      } else {
        setMobileError(response.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      setMobileError(error.response?.data?.message || 'Failed to send OTP. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    // Validate OTP
    if (!otpCode) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    if (otpCode.length !== 6) {
      setOtpError('OTP must be 6 digits');
      return;
    }

    if (verificationAttemptsRemaining <= 0) {
      setModalData({
        title: 'Too Many Attempts',
        message: 'You have entered incorrect OTP too many times. Please request a new OTP.',
        buttonText: 'Okay'
      });
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    setOtpError('');

    try {
      const response = await verifyMobileOTP(mobileNumber, otpCode);
      
      if (response.status === "success") {
        // Success! Show success modal
        setModalData({
          title: 'Mobile Verified Successfully!',
          message: 'Your mobile number has been verified. You can now access your dashboard.',
          buttonText: 'Continue to Dashboard'
        });
        setShowSuccessModal(true);
        
        // Refresh auth data to update mobile_verified flag
         sessionStorage.setItem('wiziai_mobile_verified', 'true');
        await refreshAuthData();
        
        // Navigate to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        // Wrong OTP
        const newAttemptsRemaining = verificationAttemptsRemaining - 1;
        setVerificationAttemptsRemaining(newAttemptsRemaining);
        
        if (newAttemptsRemaining > 0) {
          setOtpError(`Incorrect OTP. ${newAttemptsRemaining} attempt${newAttemptsRemaining > 1 ? 's' : ''} remaining.`);
        } else {
          setModalData({
            title: 'Too Many Attempts',
            message: 'You have entered incorrect OTP too many times. Please request a new OTP.',
            buttonText: 'Okay'
          });
          setShowErrorModal(true);
          // Reset to mobile step
          setStep('mobile');
          setOtpCode('');
        }
      }
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      setOtpError(error.response?.data?.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!canResend) {
      return;
    }

    if (attemptsRemaining <= 0) {
      setModalData({
        title: 'Rate Limit Reached',
        message: 'You have exceeded the maximum number of OTP requests for this mobile number. Please try again later or contact support.',
        buttonText: 'Okay'
      });
      setShowErrorModal(true);
      return;
    }

    setIsLoading(true);
    setOtpError('');
    setOtpCode(''); // Clear previous OTP

    try {
      const response = await resendMobileOTP(mobileNumber);
      
      if (response.status === "success") {
        const data = response.result;
        setAttemptsRemaining(data.attempts_remaining);
        setVerificationAttemptsRemaining(3); // Reset verification attempts for new OTP
        
        // Calculate new expiry time
        const expiryDate = new Date();
        expiryDate.setMinutes(expiryDate.getMinutes() + data.expires_in_minutes);
        setOtpExpiryTime(expiryDate);
        
        // Set resend countdown (60 seconds)
        setResendCountdown(60);
        setCanResend(false);
        
        setModalData({
          title: 'OTP Resent',
          message: `A new OTP has been sent to ${mobileNumber}.`,
          buttonText: 'Continue'
        });
        setShowSuccessModal(true);
      } else {
        setOtpError(response.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setOtpError(error.response?.data?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0" style={{ borderRadius: '20px' }}>
              <div className="card-body p-5">
                {/* Header */}
                <div className="text-center mb-4">
                  <div 
                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ 
                      width: '64px', 
                      height: '64px', 
                      backgroundColor: '#007bff',
                      color: '#ffffff'
                    }}
                  >
                    <i className="fas fa-mobile-alt fs-3"></i>
                  </div>
                  <h2 className="fw-bold mb-2">Mobile Verification</h2>
                  <p className="text-muted">
                    {step === 'mobile' 
                      ? 'Please verify your mobile number to continue' 
                      : `Enter the OTP sent to ${mobileNumber}`}
                  </p>
                </div>

                {/* Mobile Number Step */}
                {step === 'mobile' && (
                  <div>
                    <div className="mb-4">
                      <label htmlFor="mobileNumber" className="form-label fw-semibold">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">+91</span>
                        <input
                          type="tel"
                          className={`form-control ${mobileError ? 'is-invalid' : ''}`}
                          id="mobileNumber"
                          placeholder="Enter 10-digit mobile number"
                          value={mobileNumber}
                          onChange={handleMobileChange}
                          maxLength={10}
                          disabled={isLoading}
                        />
                      </div>
                      {mobileError && (
                        <div className="text-danger mt-2">
                          <small>{mobileError}</small>
                        </div>
                      )}
                      <div className="text-muted mt-2">
                        <small>
                          <i className="fas fa-info-circle me-1"></i>
                          Enter a valid Indian mobile number starting with 6-9
                        </small>
                      </div>
                    </div>

                    {attemptsRemaining < 3 && (
                      <div className="alert alert-warning mb-4">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> {attemptsRemaining} OTP request{attemptsRemaining !== 1 ? 's' : ''} remaining for this mobile number.
                      </div>
                    )}

                    <button
                      className="btn btn-primary w-100 py-3 fw-semibold"
                      onClick={handleSendOTP}
                      disabled={isLoading}
                      style={{ borderRadius: '12px' }}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Send OTP
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* OTP Verification Step */}
                {step === 'otp' && (
                  <div>
                    <div className="mb-4">
                      <label htmlFor="otpCode" className="form-label fw-semibold">
                        Enter OTP <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control text-center fs-4 ${otpError ? 'is-invalid' : ''}`}
                        id="otpCode"
                        placeholder="000000"
                        value={otpCode}
                        onChange={handleOtpChange}
                        maxLength={6}
                        disabled={isLoading || timeRemaining === 0}
                        style={{ 
                          letterSpacing: '0.5em',
                          fontWeight: 'bold'
                        }}
                      />
                      {otpError && (
                        <div className="text-danger mt-2">
                          <small>{otpError}</small>
                        </div>
                      )}
                    </div>

                    {/* Timer and Attempts Info */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="text-muted">
                          <small>
                            <i className="fas fa-clock me-1"></i>
                            Time remaining: <strong>{formatTimeRemaining(timeRemaining)}</strong>
                          </small>
                        </div>
                        <div className="text-muted">
                          <small>
                            <i className="fas fa-shield-alt me-1"></i>
                            Attempts: <strong>{verificationAttemptsRemaining}/3</strong>
                          </small>
                        </div>
                      </div>
                      
                      {timeRemaining === 0 && (
                        <div className="alert alert-danger mb-0">
                          <i className="fas fa-exclamation-circle me-2"></i>
                          OTP has expired. Please request a new one.
                        </div>
                      )}
                    </div>

                    {/* Verify Button */}
                    <button
                      className="btn btn-success w-100 py-3 fw-semibold mb-3"
                      onClick={handleVerifyOTP}
                      disabled={isLoading || timeRemaining === 0 || otpCode.length !== 6}
                      style={{ borderRadius: '12px' }}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Verifying...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Verify OTP
                        </>
                      )}
                    </button>

                    {/* Resend OTP */}
                    <div className="text-center">
                      {canResend ? (
                        <button
                          className="btn btn-link text-decoration-none"
                          onClick={handleResendOTP}
                          disabled={isLoading || attemptsRemaining <= 0}
                        >
                          <i className="fas fa-redo me-2"></i>
                          Resend OTP
                          {attemptsRemaining < 3 && (
                            <span className="text-muted ms-2">
                              ({attemptsRemaining} left)
                            </span>
                          )}
                        </button>
                      ) : (
                        <div className="text-muted">
                          <small>
                            Resend OTP in <strong>{resendCountdown}s</strong>
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Change Mobile Number */}
                    <div className="text-center mt-3">
                      <button
                        className="btn btn-link text-decoration-none text-secondary"
                        onClick={() => {
                          setStep('mobile');
                          setOtpCode('');
                          setOtpError('');
                          setOtpExpiryTime(null);
                        }}
                        disabled={isLoading}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Change Mobile Number
                      </button>
                    </div>
                  </div>
                )}

                {/* Warning Notice */}
                <div className="alert alert-info mt-4 mb-0" style={{ borderRadius: '12px' }}>
                  <div className="d-flex align-items-start">
                    <i className="fas fa-info-circle me-2 mt-1"></i>
                    <small>
                      <strong>Important:</strong> Mobile verification is mandatory to access your account. 
                      You must complete this step to continue.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={() => setShowSuccessModal(false)}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        title={modalData.title}
        message={modalData.message}
        buttonText={modalData.buttonText}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default MobileVerification;
